/**
 * Blossom Wings, Cart Drawer + Cart Page Controller
 *
 * UX pattern: header cart icon → /cart, add-to-bag → drawer.
 * In-place updates everywhere, no reloads.
 *
 * Inventory-aware: when Shopify rejects qty change because stock is
 * insufficient, we (1) refresh cart to reset optimistic UI, (2) show a
 * brief toast with the inventory message. Prevents the "button looks broken"
 * symptom when really it's stock blocking the change.
 *
 * Debug logs gated behind:
 *   localStorage.setItem('bw_cart_debug', '1') OR ?cart_debug=1 in URL.
 */
(function() {
  'use strict';

  var DEBUG = window.BW_CART_DEBUG === true
    || /[?&]cart_debug=1/.test(window.location.search)
    || (function() { try { return localStorage.getItem('bw_cart_debug') === '1'; } catch (e) { return false; } })();
  function log()  { if (DEBUG) { var a = ['[bw-cart]'].concat([].slice.call(arguments)); console.log.apply(console, a); } }
  function warn() { var a = ['[bw-cart]'].concat([].slice.call(arguments)); console.warn.apply(console, a); }
  function err()  { var a = ['[bw-cart]'].concat([].slice.call(arguments)); console.error.apply(console, a); }

  log('script init');

  const drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) { warn('drawer [data-cart-drawer] not found, skipping'); return; }
  log('drawer found');

  let i18n = {};
  try {
    if (drawer.dataset.i18n) i18n = JSON.parse(drawer.dataset.i18n);
  } catch (e) { warn('i18n parse failed:', e); }

  const itemsEl = drawer.querySelector('[data-cart-items]');
  const footEl = drawer.querySelector('[data-cart-foot]');
  const subtotalEl = drawer.querySelector('[data-cart-subtotal]');
  const countEl = drawer.querySelector('[data-cart-count]');
  // Free delivery from 21 Oct 2026: 2 or more packs (the 67.49 / 74.99 SAR money threshold is retired).
  // Bundles count as their component packs; map printed by snippets/bw-delivery-data.liquid.
  const freePacks = parseInt(drawer.dataset.freePacks, 10) || 2;
  let packMap = {};
  try { if (drawer.dataset.packMap) packMap = JSON.parse(drawer.dataset.packMap); } catch (e) { warn('pack map parse failed:', e); }
  function countPacks(cart) {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce(function(sum, i) { return sum + (packMap[i.handle] || 1) * (i.quantity || 0); }, 0);
  }
  const cartUrl = drawer.dataset.cartUrl || '/cart';

  // ===== Free delivery unlocked: small fireworks (owner, 21 Oct 2026) =====
  // Fires only when the bag crosses into free delivery (not on page load), on every visible
  // free delivery line (drawer top + bottom, cart page). Skipped under prefers-reduced-motion.
  let lastUnlocked = null;
  const FW_COLORS = ['#F05C85', '#F5C9D6', '#FFC94D', '#4D93C7', '#B8325A'];
  function fireworks(msg) {
    if (!msg || !msg.isConnected) return;
    const box = msg.getBoundingClientRect();
    if (!box.width || !box.height) return;
    if (getComputedStyle(msg).position === 'static') msg.style.position = 'relative';
    const host = document.createElement('span');
    host.className = 'bw-fw';
    host.setAttribute('aria-hidden', 'true');
    [0.12, 0.5, 0.88].forEach(function(x, b) {
      for (let i = 0; i < 12; i++) {
        const p = document.createElement('span');
        const a = (Math.PI * 2 * i) / 12 + Math.random() * 0.4;
        const d = 24 + Math.random() * 20;
        p.className = 'bw-fw__p';
        p.style.left = (x * 100) + '%';
        p.style.top = '50%';
        p.style.setProperty('--dx', (Math.cos(a) * d).toFixed(1) + 'px');
        p.style.setProperty('--dy', (Math.sin(a) * d).toFixed(1) + 'px');
        p.style.setProperty('--c', FW_COLORS[(i + b) % FW_COLORS.length]);
        p.style.animationDelay = (b * 140) + 'ms';
        host.appendChild(p);
      }
    });
    msg.appendChild(host);
    msg.classList.remove('bw-fw-pop'); void msg.offsetWidth; msg.classList.add('bw-fw-pop');
    setTimeout(function() { host.remove(); msg.classList.remove('bw-fw-pop'); }, 1400);
  }
  function celebrate() {
    try { if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return; } catch (e) {}
    // After add to bag the drawer opens right after this refresh: wait for it to slide in.
    setTimeout(function() {
      document.querySelectorAll('[data-shipping-progress] [data-shipping-msg]').forEach(fireworks);
    }, 380);
  }

  // ===== Delivery picker (2026-10-21, owner) =====
  // Every shopper sees Standard / Same-day / CEO with the fee, and the bag total includes it, so the
  // total does not change at checkout. Fees + free rule come from snippets/bw-delivery-rates.liquid
  // (printed on each [data-bw-dc]); the pick is saved as the cart attribute "Delivery Option" and the
  // bw-ceo-delivery checkout function moves that option to the top of the list.
  const DC_KEY = 'Delivery Option';
  const DC_CHOICES = ['standard', 'same-day', 'ceo'];
  const dcCfgEl = document.querySelector('[data-bw-dc]');
  const dcCfg = dcCfgEl ? {
    std: parseInt(dcCfgEl.dataset.std, 10) || 0,
    sd: parseInt(dcCfgEl.dataset.sd, 10) || 0,
    ceo: parseInt(dcCfgEl.dataset.ceo, 10) || 0,
    freeMin: parseInt(dcCfgEl.dataset.freeMin, 10) || 0,
    freeUntil: parseInt(dcCfgEl.dataset.freeUntil, 10) || 0,
    stdFreeMin: parseInt(dcCfgEl.dataset.stdFreeMin, 10) || 0,
    free: dcCfgEl.dataset.free || 'Free',
    cur: dcCfgEl.dataset.cur || 'SAR'
  } : null;
  let dcChoice = 'standard';
  let dcSaving = null;
  let lastCart = null;
  // Standard delivery is free when checkout makes it free: the rate itself from 65.21 net, or the
  // "Free delivery on 2+ packs" discount (40.00 net minimum) while it runs.
  function stdFree(cart) {
    if (!dcCfg || !cart) return false;
    const net = cart.total_price || 0;
    if (dcCfg.stdFreeMin && net >= dcCfg.stdFreeMin) return true;
    return !!dcCfg.freeMin && net >= dcCfg.freeMin && Date.now() / 1000 <= dcCfg.freeUntil;
  }
  function feeFor(cart, choice) {
    if (!dcCfg || !cart || !cart.item_count) return 0;
    if (choice === 'same-day') return dcCfg.sd;
    if (choice === 'ceo') return dcCfg.ceo;
    return stdFree(cart) ? 0 : dcCfg.std;
  }
  // Shipping is not taxed, so fees are shown as they are (no VAT gross-up).
  function fmtFee(cents) {
    return cents === 0 ? dcCfg.free : (cents / 100).toFixed(2) + ' ' + (i18n.currency || dcCfg.cur);
  }
  // Same wording as checkout (bw-ceo-delivery): Riyadh time (UTC+3), 2 PM cutoff, no Friday delivery.
  function sameDayMode() {
    const r = new Date(Date.now() + 3 * 3600 * 1000);
    const day = r.getUTCDay(), past = r.getUTCHours() >= 14;
    if (day === 5 || (day === 4 && past)) return 'sat';
    return past ? 'nd' : 'sd';
  }
  function renderDelivery(cart) {
    if (!dcCfg || !cart) return;
    lastCart = cart;
    const saved = cart.attributes && cart.attributes[DC_KEY];
    if (!dcSaving && DC_CHOICES.indexOf(saved) !== -1) dcChoice = saved;
    const mode = sameDayMode();
    document.querySelectorAll('[data-bw-dc]').forEach(function(box) {
      box.querySelectorAll('[data-dc-radio]').forEach(function(r) { r.checked = r.value === dcChoice; });
      box.querySelectorAll('[data-dc-fee]').forEach(function(f) { f.textContent = fmtFee(feeFor(cart, f.dataset.dcFee)); });
      const t = box.querySelector('[data-dc-sd-title]'), d = box.querySelector('[data-dc-sd-desc]');
      if (t && box.dataset[mode + 'T']) t.textContent = box.dataset[mode + 'T'];
      if (d && box.dataset[mode + 'D']) d.textContent = box.dataset[mode + 'D'];
    });
    const fee = feeFor(cart, dcChoice);
    const total = grossCents(cart.total_price) + fee;
    const totalStr = (total / 100).toFixed(2) + ' ' + (i18n.currency || dcCfg.cur);
    document.querySelectorAll('[data-dc-fee-row]').forEach(function(n) { n.textContent = fmtFee(fee); });
    document.querySelectorAll('[data-dc-total], [data-cart-page-total]').forEach(function(n) { n.textContent = totalStr; });
    const ct = document.querySelector('[data-cart-page-checkout-total]');
    if (ct) ct.textContent = '· ' + totalStr;
    document.querySelectorAll('[data-dc-input]').forEach(function(n) { n.value = dcChoice; });
  }
  function saveChoice(choice) {
    dcChoice = choice;
    if (lastCart) renderDelivery(lastCart);
    const attrs = {}; attrs[DC_KEY] = choice;
    const p = fetch(cartUrl + '/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).then(function(r) { return r.ok ? r.json() : null; })
      .then(function(cart) { if (dcSaving === p) { dcSaving = null; if (cart) renderDelivery(cart); } })
      .catch(function(e2) { warn('delivery choice save failed', e2); if (dcSaving === p) dcSaving = null; });
    dcSaving = p;
    return p;
  }
  document.addEventListener('change', function(e) {
    const r = e.target.closest('[data-dc-radio]');
    if (r && r.checked) saveChoice(r.value);
  });
  // Checkout from the drawer: make sure the pick is saved before leaving the page.
  document.addEventListener('click', function(e) {
    const a = e.target.closest('.nr-cart__checkout');
    if (!a || !dcSaving) return;
    e.preventDefault();
    const go = function() { window.location.href = a.href; };
    dcSaving.then(go, go);
  });
  const isCartPage = window.location.pathname === '/cart' || window.location.pathname.endsWith('/cart');

  // ===== Toast (inventory-aware error UI) =====
  function showToast(message, kind) {
    kind = kind || 'info';
    var toast = document.createElement('div');
    toast.className = 'bw-toast bw-toast--' + kind;
    toast.textContent = message;
    toast.setAttribute('role', 'alert');
    document.body.appendChild(toast);
    requestAnimationFrame(function() { toast.classList.add('is-visible'); });
    setTimeout(function() {
      toast.classList.remove('is-visible');
      setTimeout(function() { toast.remove(); }, 350);
    }, 3500);
  }

  // ===== Drawer open/close =====
  function openDrawer() {
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('bw-no-scroll');
  }
  function closeDrawer() {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('bw-no-scroll');
  }
  document.addEventListener('click', function(e) {
    if (e.target.closest('[data-drawer-close]')) closeDrawer();
  });
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
  });

  // ===== Add to cart =====
  document.addEventListener('submit', async function(e) {
    const form = e.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    e.preventDefault();
    const submitBtn = form.querySelector('button[type="submit"]');
    // innerHTML, not textContent: during a sale the PDP button label carries a struck price (2026-10-19).
    const originalLabel = submitBtn ? submitBtn.innerHTML : null;
    if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = (i18n.adding || 'Adding') + '…'; }
    try {
      const res = await fetch(cartUrl + '/add.js', { method: 'POST', body: new FormData(form), headers: { 'Accept': 'application/json' } });
      if (!res.ok) {
        let data = null;
        try { data = await res.json(); } catch (e2) {}
        const msg = (data && (data.description || data.message)) || ('Could not add to bag (' + res.status + ')');
        log('add.js error', { status: res.status, data: data });
        showToast(msg, 'error');
        await refreshCart();
        if (submitBtn) { submitBtn.textContent = i18n.retry || 'Try again'; submitBtn.disabled = false; setTimeout(function() { if (submitBtn) submitBtn.innerHTML = originalLabel; }, 2000); }
        return;
      }
      log('add.js ok');
      try {
        var added = await res.clone().json();
        document.dispatchEvent(new CustomEvent('bw:cart:added', { detail: added }));
      } catch (eAdded) {}
      // Cart page rows are server-rendered; a new line (e.g. from the cart cross-sell)
      // only shows after a reload, so reload instead of opening the drawer there.
      if (isCartPage) { window.location.reload(); return; }
      await refreshCart();
      openDrawer();
      if (submitBtn) {
        submitBtn.textContent = '✓ ' + (i18n.added || 'Added');
        setTimeout(function() { submitBtn.innerHTML = originalLabel; submitBtn.disabled = false; }, 1500);
      }
    } catch (e2) {
      err('add to cart error', e2);
      showToast(i18n.addError || 'Could not add to bag, please try again.', 'error');
      if (submitBtn) { submitBtn.textContent = i18n.retry || 'Try again'; submitBtn.disabled = false; }
    }
  });

  // ===== Quantity change & remove (delegated) =====
  document.addEventListener('click', async function(e) {
    const qtyBtn = e.target.closest('[data-qty-change]');
    if (qtyBtn) {
      const item = qtyBtn.closest('[data-cart-item]');
      if (!item) return;
      e.preventDefault();
      const input = item.querySelector('[data-qty-input]');
      if (!input) return;
      const currentQty = parseInt(input.value, 10) || 1;
      const change = parseInt(qtyBtn.dataset.qtyChange, 10) || 0;
      const newQty = Math.max(0, currentQty + change);
      log('qty click', { key: item.dataset.key, currentQty: currentQty, change: change, newQty: newQty });
      input.value = newQty;
      qtyBtn.disabled = true;
      try {
        await updateLine(item.dataset.key, newQty);
      } finally {
        qtyBtn.disabled = false;
      }
      return;
    }
    const removeBtn = e.target.closest('[data-remove]');
    if (removeBtn) {
      const item = removeBtn.closest('[data-cart-item]');
      if (!item) return;
      e.preventDefault();
      await updateLine(item.dataset.key, 0);
    }
  });

  document.addEventListener('change', async function(e) {
    const input = e.target.closest('[data-qty-input]');
    if (!input) return;
    const item = input.closest('[data-cart-item]');
    if (!item) return;
    await updateLine(item.dataset.key, Math.max(0, parseInt(input.value, 10) || 0));
  });

  async function updateLine(key, quantity) {
    if (!key) { warn('updateLine missing key'); return; }
    try {
      log('POST /cart/change.js', { id: key, quantity: quantity });
      const res = await fetch(cartUrl + '/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      });
      if (!res.ok) {
        let data = null;
        try { data = await res.json(); } catch (e2) {}
        const msg = (data && (data.description || data.message)) || ('Could not update cart (' + res.status + ')');
        log('change.js error', { status: res.status, data: data });
        showToast(msg, 'error');
        await refreshCart(); // critical: reset optimistic UI to actual cart state
        return;
      }
      const data = await res.json();
      log('change.js ok', { item_count: data.item_count, total_price: data.total_price });
      await refreshCart();
    } catch (e2) {
      err('updateLine error', e2);
      showToast('Could not update cart, please try again.', 'error');
      await refreshCart();
    }
  }

  async function refreshCart() {
    try {
      const cart = await (await fetch(cartUrl + '.js', { headers: { 'Accept': 'application/json' } })).json();
      log('cart fetched', { item_count: cart.item_count, total_price: cart.total_price });
      renderDrawer(cart);
      renderCartPage(cart);
      renderDelivery(cart);
      updateShippingBars(cart);
      updateHeaderBadge(cart.item_count);
      // Cart cross-sell (snippets/bw-cart-xsell) re-picks its suggestion from this.
      window.bwCartState = cart;
      try { document.dispatchEvent(new CustomEvent('bw:cart:refreshed', { detail: cart })); } catch (e3) {}
    } catch (e2) {
      err('refreshCart error', e2);
    }
  }

  // Store prices are stored net of VAT (Shopify adds 15% at checkout), but every
  // price we show the shopper is the VAT-inclusive shelf price. Every caller here
  // passes net cents straight from the cart API, so gross up in one place.
  function grossCents(cents) { return Math.round(cents * 115 / 100); }

  function formatMoney(cents) {
    const amt = (grossCents(cents) / 100).toFixed(2);
    const code = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'SAR';
    return amt + ' ' + (i18n.currency || code);
  }
  function escapeHtml(str) { const div = document.createElement('div'); div.textContent = str || ''; return div.innerHTML; }

  // Sale strikethrough for a cart line (2026-10-19, owner: "make all prices strikethrough prices").
  // Only while the line's product is in the active sale window (window.bwOffer, printed by
  // snippets/bw-offer-data.liquid); outside a window the line shows final_line_price alone, as before.
  // Shown price: Shopify's own final_line_price when the automatic discount is already on the line
  // (cart.js reflects it, so this is what checkout charges, whatever discounts combine). If the cart
  // does not carry the discount (e.g. the temp preview window, no real discount behind it), the same
  // maths as checkout is used: qty x (unit net - floor(unit net x pct / 100)). Struck price: the line
  // before discounts. Free-delivery logic (packs) is untouched. Twin: snippets/bw-cart-item.liquid.
  function offerLine(net, origNet, unitOrigNet, qty, handle) {
    const p = window.bwOffer ? window.bwOffer.pct(handle) : 0;
    if (!(p > 0) || !(origNet > 0)) return null;
    const now = net < origNet ? net : window.bwOffer.unitNet(unitOrigNet, p) * qty;
    if (!(now < origNet)) return null;
    return { now: now, was: origNet, pct: p };
  }
  function linePriceHtml(item) {
    const o = offerLine(item.final_line_price, item.original_line_price, item.original_price, item.quantity, item.handle);
    if (!o) return escapeHtml(formatMoney(item.final_line_price));
    return window.bwOffer.html(formatMoney(o.now), formatMoney(o.was), o.pct);
  }
  function unitPriceHtml(item) {
    const o = offerLine(item.final_price, item.original_price, item.original_price, 1, item.handle);
    if (!o) return escapeHtml(formatMoney(item.final_price));
    return window.bwOffer.html(formatMoney(o.now), formatMoney(o.was), o.pct);
  }

  function updateHeaderBadge(count) {
    const headerCart = document.querySelector('.bw-header__cart');
    let headerBadge = document.querySelector('.bw-header__cart-count');
    if (count > 0) {
      if (!headerBadge && headerCart) {
        headerBadge = document.createElement('span');
        headerBadge.className = 'bw-header__cart-count';
        headerCart.appendChild(headerBadge);
      }
      if (headerBadge) { headerBadge.textContent = count; headerBadge.style.display = ''; }
    } else if (headerBadge) {
      headerBadge.style.display = 'none';
    }
  }

  function renderDrawer(cart) {
    if (!itemsEl || !footEl) return;
    if (countEl) countEl.textContent = cart.item_count;
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

    if (cart.item_count === 0) {
      itemsEl.innerHTML = `
        <div class="bw-cart-empty">
          <div class="bw-cart-empty__icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M6 7h12l-1.2 11.4a2 2 0 01-2 1.6H9.2a2 2 0 01-2-1.6L6 7z"/>
              <path d="M9 10V6a3 3 0 016 0v4"/>
            </svg>
          </div>
          <h3>${i18n.emptyTitle || 'Your bag is empty'}</h3>
          <p>${i18n.emptyMessage || 'Add some softness to your day.'}</p>
          <a href="${i18n.collectionsUrl || '/collections/all'}" class="bw-btn bw-btn--primary" data-drawer-close>${i18n.emptyCta || 'Shop the collection'}</a>
        </div>
      `;
      footEl.hidden = true;
      drawer.querySelectorAll('[data-cart-after]').forEach(function(n) { n.hidden = true; });
    } else {
      itemsEl.innerHTML = cart.items.map(function(item) { return `
        <article class="bw-cart-item" data-cart-item data-key="${item.key}">
          <a href="${item.url}" class="bw-cart-item__media">
            ${item.image ? `<img src="${item.image}" width="100" height="100" alt="${escapeHtml(item.product_title)}" class="bw-cart-item__img" loading="lazy">` : ''}
          </a>
          <div class="bw-cart-item__body">
            <a href="${item.url}" class="bw-cart-item__title">${escapeHtml(item.product_title)}</a>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="bw-cart-item__variant">${escapeHtml(item.variant_title)}</p>` : ''}
            <div class="bw-cart-item__bottom">
              <div class="bw-cart-item__qty">
                <button type="button" class="bw-cart-item__qty-btn" data-qty-change="-1" aria-label="${escapeHtml(i18n.decrease || 'Decrease')}">−</button>
                <input type="number" class="bw-cart-item__qty-input" value="${item.quantity}" min="0" aria-label="${escapeHtml(i18n.quantity || 'Quantity')}" data-qty-input>
                <button type="button" class="bw-cart-item__qty-btn" data-qty-change="1" aria-label="${escapeHtml(i18n.increase || 'Increase')}">+</button>
              </div>
              <span class="bw-cart-item__price">${linePriceHtml(item)}</span>
            </div>
            <button type="button" class="bw-cart-item__remove" data-remove>${i18n.remove || 'Remove'}</button>
          </div>
        </article>
      `; }).join('');
      footEl.hidden = false;
      drawer.querySelectorAll('[data-cart-after]').forEach(function(n) { n.hidden = false; });
    }
  }

  function renderCartPage(cart) {
    if (!isCartPage) return;
    const rows = document.querySelectorAll('[data-cart-item][data-cart-page-row]');
    rows.forEach(function(row) {
      const key = row.dataset.key;
      const vid = row.dataset.variantId;
      // Match by line key first; fall back to variant id because Shopify line
      // keys are NOT guaranteed stable across change.js calls. Without the
      // fallback a shifted key makes find() miss and the row gets wrongly removed.
      let item = cart.items.find(function(i) { return i.key === key; });
      if (!item && vid) {
        item = cart.items.find(function(i) { return String(i.variant_id || i.id) === vid; });
      }
      if (!item) { row.remove(); return; } // genuinely gone from the cart
      // Self-heal: store the current key so the next qty update posts a valid id.
      if (item.key && item.key !== key) row.dataset.key = item.key;
      const totalEl = row.querySelector('[data-cart-line-total]');
      if (totalEl) totalEl.innerHTML = linePriceHtml(item);
      const qtyInput = row.querySelector('[data-qty-input]');
      if (qtyInput && document.activeElement !== qtyInput) qtyInput.value = item.quantity;
      const unitEl = row.querySelector('[data-cart-line-unit]');
      if (unitEl) unitEl.innerHTML = unitPriceHtml(item);
    });
    const pageSubtotal = document.querySelector('[data-cart-page-subtotal]');
    if (pageSubtotal) pageSubtotal.textContent = formatMoney(cart.total_price);
    const pageTotal = document.querySelector('[data-cart-page-total]');
    if (pageTotal) pageTotal.textContent = formatMoney(cart.total_price);
    const checkoutTotal = document.querySelector('[data-cart-page-checkout-total]');
    if (checkoutTotal) checkoutTotal.textContent = '· ' + formatMoney(cart.total_price);
    if (cart.item_count === 0) {
      const layout = document.querySelector('.bw-cart-page__layout');
      const shipping = document.querySelector('.bw-cart-page__shipping');
      const empty = document.querySelector('[data-cart-page-empty]');
      if (layout) layout.style.display = 'none';
      if (shipping) shipping.style.display = 'none';
      if (empty) empty.style.display = '';
      const continueLink = document.querySelector('.bw-cart-page__continue');
      if (continueLink) continueLink.style.display = 'none';
      const headerCount = document.querySelector('.bw-cart-page__count');
      if (headerCount) headerCount.style.display = 'none';
    }
  }

  function updateShippingBars(cart) {
    const packs = countPacks(cart);
    // Unlocked = checkout really makes Standard delivery free (stdFree), so the message and the fee agree.
    const unlocked = dcCfg ? stdFree(cart) : packs >= freePacks;
    if (lastUnlocked === false && unlocked) celebrate();
    lastUnlocked = unlocked;
    document.querySelectorAll('[data-shipping-progress]').forEach(function(node) {
      const msg = node.querySelector('[data-shipping-msg]');
      const fill = node.querySelector('[data-shipping-fill]');
      if (node.dataset.hideEmpty === '1') node.hidden = packs === 0;
      if (fill) fill.style.width = (unlocked ? 100 : Math.min(100, (packs / freePacks) * 100)) + '%';
      if (msg) {
        if (unlocked) {
          msg.innerHTML = i18n.shippingUnlocked || '<strong>Free delivery unlocked</strong>';
        } else if (packs === freePacks - 1) {
          msg.innerHTML = i18n.shippingAddOne || 'Add 1 more pack for <strong>free delivery</strong>';
        } else {
          msg.innerHTML = i18n.shippingRule || 'Free delivery on 2 or more packs';
        }
      }
    });
  }

  refreshCart();
  log('ready');
})();

// Toast styles, injected once, lives in same asset
(function() {
  if (document.getElementById('bw-toast-styles')) return;
  var s = document.createElement('style');
  s.id = 'bw-toast-styles';
  s.textContent = `
.bw-toast {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%) translateY(20px);
  background: #1a2348;
  color: #fff;
  padding: 14px 22px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.4;
  max-width: calc(100% - 32px);
  box-shadow: 0 8px 28px rgba(0,0,0,0.2);
  opacity: 0;
  transition: opacity 0.25s ease, transform 0.25s ease;
  z-index: 9999;
  pointer-events: none;
}
.bw-toast.is-visible { opacity: 1; transform: translateX(-50%) translateY(0); }
.bw-toast--error { background: #b54a3a; }
.bw-toast--success { background: #2a7a4f; }
.bw-fw { position: absolute; inset: 0; pointer-events: none; overflow: visible; z-index: 3; }
.bw-fw__p { position: absolute; width: 6px; height: 6px; margin: -3px 0 0 -3px; box-shadow: 0 0 4px var(--c); border-radius: 50%; background: var(--c); opacity: 0; animation: bw-fw 1100ms cubic-bezier(.2,.7,.3,1) forwards; }
@keyframes bw-fw { 0% { transform: translate(0, 0) scale(.5); opacity: 1; } 70% { opacity: 1; } 100% { transform: translate(var(--dx), var(--dy)) scale(.25); opacity: 0; } }
.bw-fw-pop { animation: bw-fw-pop 500ms ease-out; }
@keyframes bw-fw-pop { 0% { transform: scale(1); } 40% { transform: scale(1.08); } 100% { transform: scale(1); } }
@media (max-width: 600px) {
  .bw-toast { bottom: 16px; padding: 12px 18px; font-size: 13.5px; }
}
`;
  document.head.appendChild(s);
})();
